<script lang="ts">
  import { router, page, Link } from '@inertiajs/svelte';
  import { tick, untrack } from 'svelte';
  import { computePosition, flip, shift, offset } from '@floating-ui/dom';
  import type {
    ProjectWithTasks,
    Project,
    Task,
    TaskPriority,
    TaskViewPreferences,
    ProjectCategory,
    ProjectStatus,
  } from '../../lib/types';
  import {
    categoryColors,
    categoryIcons,
    parseCalendarDate,
    formatCalendarDate,
    getWeekKey,
  } from '../../lib/utils';
  import { sortTasks, groupTasks, isDragReorderAllowed } from '../../lib/taskSort';
  import { buildTasksViewUrl, parseTasksViewFromUrl } from '../../lib/tasksViewMode';
  import { setCompactTaskDragImage } from '../../lib/dragImage';
  import momentiumLogo from '../../assets/momentium-logo.png';
  import {
    openProjectModalForEdit,
    openProjectScheduleModal,
    openProjectModalForConvertFromTask,
  } from '../../lib/stores/ui';
  import { jsonHeaders, readApiErrorMessage } from '../../lib/apiHelpers';
  import { recentlyAddedTasks, markTaskRecentlyAdded } from '../../lib/stores/recentlyAddedTask';
  import { trackTaskCreation } from '../../lib/pendingTaskCreations';
  import {
    setProjectTasks,
    invalidateProjectTasks,
    subscribeToCachedTasks,
  } from '../../lib/stores/projectTasksCache';
  import { undoableAction, undoableSoftDelete } from '../../lib/undoManager';
  import {
    completeTask,
    markTaskWontDo,
    duplicateTask,
    deleteTask as deleteTaskApi,
    moveTaskToProject as moveTaskToProjectApi,
  } from '../../lib/taskApi';
  import { toast } from 'svelte-sonner';
  import ProfileDropdown from '../../components/ProfileDropdown.svelte';
  import NavDropdown from '../../components/NavDropdown.svelte';
  import ProjectModal from '../../components/ProjectModal.svelte';
  import MobileTopBar from '../../components/mobile/MobileTopBar.svelte';
  import { tapAlternatives, viewport } from '../../lib/stores/viewport';
  import { moveUpDownActions, type MoveDirection, type SheetAction } from '../../lib/sheetActions';
  import MobileAddTask, { type NewTaskDraft } from '../../components/mobile/MobileAddTask.svelte';
  import TaskViewOptions from '../../components/TaskViewOptions.svelte';
  import CopyTasksMenu from '../../components/CopyTasksMenu.svelte';
  import TaskDetailPopover from '../../components/TaskDetailPopover.svelte';
  import ProjectKanbanBoard from '../../components/ProjectKanbanBoard.svelte';
  import MoveToProjectPicker from '../../components/MoveToProjectPicker.svelte';
  import MoveToProjectPanel from '../../components/MoveToProjectPanel.svelte';
  import { removeTaskAfterConversion } from '../../lib/taskActions';
  import Icon from '../../components/Icon.svelte';
  import TaskCopyActionRow from '../../components/TaskCopyActionRow.svelte';
  import RecentMoveMenuItem from '../../components/RecentMoveMenuItem.svelte';
  import { recordRecentMove } from '../../lib/stores/recentMove';
  import BulkMoveTasksConfirmModal from '../../components/BulkMoveTasksConfirmModal.svelte';

  interface Props {
    project: ProjectWithTasks;
  }

  let { project }: Props = $props();

  // Task view preferences
  const defaultPrefs: TaskViewPreferences = {
    sort_by: 'position',
    sort_direction: 'asc',
    group_by: 'none',
  };
  let taskViewPrefs = $state<TaskViewPreferences>(
    ($page.props as any).preferences?.taskView ?? defaultPrefs,
  );

  // eslint-disable-next-line momentium/no-unguarded-effect -- Inertia prop sync: uses untrack + identity guard per EFFECT_GUIDELINES.md Rule 4 & 7
  $effect(() => {
    const serverPrefs = ($page.props as any).preferences?.taskView;
    if (serverPrefs) {
      taskViewPrefs = serverPrefs;
    }
  });

  function handlePrefsUpdate(newPrefs: TaskViewPreferences) {
    taskViewPrefs = newPrefs;
  }

  function persistPrefs(prefs: TaskViewPreferences) {
    fetch('/preferences/task_view', {
      method: 'PATCH',
      headers: jsonHeaders(),
      body: JSON.stringify(prefs),
    }).catch((e) => console.error('Failed to save preferences:', e));
  }

  function clearSortPref() {
    const updated: TaskViewPreferences = {
      ...taskViewPrefs,
      sort_by: 'position',
      sort_direction: 'asc',
    };
    handlePrefsUpdate(updated);
    persistPrefs(updated);
  }

  function clearGroupPref() {
    const updated: TaskViewPreferences = { ...taskViewPrefs, group_by: 'none' };
    handlePrefsUpdate(updated);
    persistPrefs(updated);
  }

  let canDragReorder = $derived(isDragReorderAllowed(taskViewPrefs));

  // View mode: list (checklist) or board (kanban)
  type ViewMode = 'list' | 'board';

  function getInitialViewMode(): ViewMode {
    if (typeof window === 'undefined') return 'list';
    return parseTasksViewFromUrl(window.location.href) ?? 'list';
  }

  function persistViewModeToUrl(mode: ViewMode) {
    if (typeof window === 'undefined') return;
    window.history.replaceState(
      window.history.state,
      '',
      buildTasksViewUrl(window.location.href, mode),
    );
  }

  let viewMode = $state<ViewMode>(getInitialViewMode());

  function setViewMode(mode: ViewMode) {
    if (viewMode === mode) return;
    viewMode = mode;
    persistViewModeToUrl(mode);
  }

  // Keep view mode in sync with URL on Inertia navigations.
  // eslint-disable-next-line momentium/no-unguarded-effect -- sync local view mode from URL query param on navigation
  $effect(() => {
    const modeFromUrl = parseTasksViewFromUrl($page.url);
    viewMode = modeFromUrl ?? 'list';
  });

  function handleTasksChangeFromKanban(newTasks: Task[]) {
    setProjectTasks(project.id, newTasks);
  }

  // Reorder confirmation modal
  let pendingReorder = $state<{ fromIdx: number; toIdx: number } | null>(null);

  // Local tasks state — managed locally for optimistic updates (no Inertia).
  // Re-syncs only when navigating to a *different* project (project.id changes).
  // svelte-ignore state_referenced_locally
  let tasks = $state<Task[]>(project.tasks || []);
  // svelte-ignore state_referenced_locally
  let syncedProjectId = $state(project.id);

  // Seed the shared cache on first mount (intentionally uses initial values)
  // svelte-ignore state_referenced_locally
  setProjectTasks(syncedProjectId, tasks);

  // Re-sync when Inertia navigates to a different project (id changes)
  // eslint-disable-next-line momentium/no-unguarded-effect -- Inertia prop sync: uses untrack + identity guard to sync project tasks on navigation
  $effect(() => {
    const currentId = project.id;
    if (currentId !== syncedProjectId) {
      untrack(() => {
        syncedProjectId = currentId;
        tasks = project.tasks || [];
        setProjectTasks(currentId, tasks);
      });
    }
  });

  // Cache is the source of truth for undo/rollback. One-way only: do not write
  // the cache from this callback (and do not read `tasks` here — subscribe fires
  // synchronously and a read would track `tasks`).
  // eslint-disable-next-line momentium/no-unguarded-effect -- external subscription: projectTasksCache pub/sub, re-subscribes on project id change, returns cleanup
  $effect(() => {
    const id = project.id;
    return subscribeToCachedTasks(id, (entry) => {
      if (entry.fetched) tasks = entry.tasks;
    });
  });

  // Tasks derived from local state
  let rawIncompleteTasks = $derived(tasks.filter((t) => t.status === 'todo'));
  let incompleteTasks = $derived(sortTasks(rawIncompleteTasks, taskViewPrefs));
  let taskGroups = $derived(groupTasks(rawIncompleteTasks, taskViewPrefs));
  let isGrouped = $derived(taskViewPrefs.group_by !== 'none');

  let completedTasks = $derived(
    tasks
      .filter((t) => t.status !== 'todo')
      .sort((a, b) => {
        const aTime = a.completedAt ? new Date(a.completedAt).getTime() : 0;
        const bTime = b.completedAt ? new Date(b.completedAt).getTime() : 0;
        return bTime - aTime;
      }),
  );

  let showCompleted = $derived(taskViewPrefs.show_completed ?? false);
  let newTaskTitle = $state('');
  let newTaskInput: HTMLInputElement | undefined = $state();
  let isAddingTask = $state(false);

  // Expanded add-task form (expand button or Shift+Enter)
  let isAddFormExpanded = $state(false);
  let newTaskNotes = $state('');
  let newTaskPriority = $state<TaskPriority>('none');
  let newTaskNotesInput: HTMLTextAreaElement | undefined = $state();

  function expandAddForm() {
    if (!newTaskTitle.trim()) return;
    isAddFormExpanded = true;
    tick().then(() => newTaskNotesInput?.focus());
  }

  function collapseAddForm() {
    isAddFormExpanded = false;
    newTaskNotes = '';
    newTaskPriority = 'none';
  }

  // Track which task's menu is open (null = all closed)
  let openMenuId = $state<string | null>(null);
  // Track which task's "move to project" picker is open
  let movePickerTaskId = $state<string | null>(null);

  // Bulk move incomplete tasks UI state
  let bulkMovePickerOpen = $state(false);
  let bulkMoveAnchorEl = $state<HTMLElement | null>(null);
  let bulkMoveTargetProjectId = $state<string | null>(null);
  let bulkMoveTargetProjectTitle = $state('');
  let showBulkMoveConfirm = $state(false);

  function toggleMenu(taskId: string) {
    openMenuId = openMenuId === taskId ? null : taskId;
    movePickerTaskId = null;
  }

  function closeMenus() {
    openMenuId = null;
    movePickerTaskId = null;
    bulkMovePickerOpen = false;
    bulkMoveAnchorEl = null;
  }

  // === Task Actions (all via fetch + optimistic updates, no Inertia) ===

  async function addTask() {
    if (!newTaskTitle.trim() || isAddingTask) return;
    isAddingTask = true;

    const title = newTaskTitle.trim();
    const notes = isAddFormExpanded ? newTaskNotes.trim() || null : null;
    const priority = isAddFormExpanded ? newTaskPriority : ('none' as TaskPriority);
    newTaskTitle = '';
    collapseAddForm();

    try {
      await createTask({ title, notes, priority, tagNames: [] });
    } finally {
      isAddingTask = false;
      await tick();
      newTaskInput?.focus();
    }
  }

  /** Optimistic add, shared by the inline input (desktop) and MobileAddTask's sheet. */
  async function createTask({ title, notes, priority, tagNames }: NewTaskDraft) {
    const tempId = `temp-${crypto.randomUUID()}`;
    const tempTask: Task = {
      id: tempId,
      projectId: project.id,
      title,
      notes,
      status: 'todo',
      priority,
      tags: tagNames.map((name) => ({ id: name, name })),
      position: incompleteTasks.length,
      isActive: false,
      completedAt: null,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    tasks = [...tasks, tempTask];
    setProjectTasks(project.id, tasks);
    markTaskRecentlyAdded(tempId);
    const settleCreation = trackTaskCreation(tempId);
    let saved: Task | null = null;

    try {
      const response = await fetch(`/projects/${project.id}/tasks`, {
        method: 'POST',
        headers: jsonHeaders(),
        body: JSON.stringify({
          task: { title, notes, priority, ...(tagNames.length ? { tag_names: tagNames } : {}) },
        }),
      });
      if (response.ok) {
        const created: Task = await response.json();
        tasks = tasks.map((t) => (t.id === tempId ? created : t));
        setProjectTasks(project.id, tasks);
        markTaskRecentlyAdded(created.id);
        saved = created;
      } else {
        tasks = tasks.filter((t) => t.id !== tempId);
        setProjectTasks(project.id, tasks);
        toast.error(await readApiErrorMessage(response, 'Failed to add task'));
      }
    } catch {
      tasks = tasks.filter((t) => t.id !== tempId);
      setProjectTasks(project.id, tasks);
      toast.error('Failed to add task');
    } finally {
      // Always settle, or mutations queued on this row would wait forever.
      settleCreation(saved);
    }
  }

  async function toggleTask(task: Task) {
    await toggleTaskWithUpdates(task);
  }

  async function toggleTaskWithUpdates(task: Task, pendingUpdates?: Partial<Task>) {
    await completeTask(project.id, task.id, tasks, pendingUpdates);
  }

  async function handleDuplicateTask(task: Task) {
    closeMenus();
    await duplicateTask(project.id, task.id, tasks);
  }

  async function markWontDo(task: Task) {
    await markWontDoWithUpdates(task);
  }

  async function markWontDoWithUpdates(task: Task, pendingUpdates?: Partial<Task>) {
    closeMenus();
    await markTaskWontDo(project.id, task.id, tasks, pendingUpdates);
  }

  function deleteTask(task: Task) {
    closeMenus();
    deleteTaskApi(project.id, task.id, tasks);
  }

  function requestDeleteTask(task: Task) {
    closeMenus();
    deleteTask(task);
  }

  function convertTaskToProject(task: Task) {
    closeMenus();
    openProjectModalForConvertFromTask(task.title, task.notes || '', () =>
      removeTaskAfterConversion(task),
    );
  }

  async function setPriority(task: Task, newPriority: TaskPriority) {
    if (task.priority === newPriority) return;
    closeMenus();
    const prevTasks = [...tasks];

    tasks = tasks.map((t) => (t.id === task.id ? { ...t, priority: newPriority } : t));
    setProjectTasks(project.id, tasks);

    try {
      const response = await fetch(`/projects/${project.id}/tasks/${task.id}`, {
        method: 'PATCH',
        headers: jsonHeaders(),
        body: JSON.stringify({ task: { priority: newPriority } }),
      });
      if (response.ok) {
        const updated: Task = await response.json();
        tasks = tasks.map((t) => (t.id === task.id ? updated : t));
        setProjectTasks(project.id, tasks);
      } else {
        tasks = prevTasks;
        setProjectTasks(project.id, prevTasks);
        toast.error('Failed to update priority');
      }
    } catch {
      tasks = prevTasks;
      setProjectTasks(project.id, prevTasks);
      toast.error('Failed to update priority');
    }
  }

  function moveTaskToProject(task: Task, targetProjectId: string, targetProjectTitle?: string) {
    closeMenus();
    movePickerTaskId = null;
    moveTaskToProjectApi(project.id, targetProjectId, task.id, tasks, { targetProjectTitle });
  }

  /** Direct move via the recent-move shortcut (no picker). */
  function moveTaskToRecent(task: Task, targetProjectId: string, targetProjectTitle: string) {
    moveTaskToProject(task, targetProjectId, targetProjectTitle);
  }

  async function confirmBulkMoveIncompleteTasks() {
    if (!bulkMoveTargetProjectId) return;

    showBulkMoveConfirm = false;

    try {
      const response = await fetch(`/projects/${project.id}/tasks/move_incomplete`, {
        method: 'POST',
        headers: jsonHeaders(),
        body: JSON.stringify({ target_project_id: bulkMoveTargetProjectId }),
      });

      if (!response.ok) throw new Error('Failed to move tasks');

      tasks = tasks.filter((t) => t.status !== 'todo');
      setProjectTasks(project.id, tasks);
      invalidateProjectTasks(bulkMoveTargetProjectId);
      // Only record once the bulk move actually landed (the confirm modal can be cancelled).
      if (bulkMoveTargetProjectTitle) {
        recordRecentMove(bulkMoveTargetProjectId, bulkMoveTargetProjectTitle);
      }
      bulkMoveTargetProjectId = null;
      bulkMoveTargetProjectTitle = '';
      toast.success('Moved incomplete tasks');
    } catch {
      toast.error('Failed to move incomplete tasks');
    }
  }

  // === Drag reorder ===
  let dragIdx = $state<number | null>(null);
  let dragOverIdx = $state<number | null>(null);

  function handleDragStart(e: DragEvent, idx: number) {
    dragIdx = idx;
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', String(idx));
      const task = incompleteTasks[idx];
      setCompactTaskDragImage(e, task.title, task.priority);
    }
  }

  function handleDragOver(e: DragEvent, idx: number) {
    e.preventDefault();
    dragOverIdx = idx;
  }

  function handleDrop(e: DragEvent, idx: number) {
    e.preventDefault();
    if (dragIdx === null || dragIdx === idx) return;
    requestReorder(dragIdx, idx);
  }

  /** Drop and Move up / Move down: reorders, after confirming when a sort is active. */
  function requestReorder(fromIdx: number, toIdx: number) {
    // If non-default sort/group is active, show confirmation modal
    if (!canDragReorder) {
      pendingReorder = { fromIdx, toIdx };
      dragIdx = null;
      dragOverIdx = null;
      return;
    }

    executeReorder(fromIdx, toIdx);
  }

  function taskMoveActions(idx: number): SheetAction[] {
    return moveUpDownActions(idx, incompleteTasks.length, (direction: MoveDirection) =>
      requestReorder(idx, idx + direction),
    );
  }

  // Mobile: a task row's ⋯ opens the task sheet on its own ⋯ menu, so both are
  // the same menu; the row adds Move up / Move down (the tap path for drag-to-reorder).
  function openTaskMenu(task: Task, idx: number, trigger: HTMLElement) {
    if (!$viewport.isMobile) {
      toggleMenu(task.id);
      return;
    }
    closeMenus();
    openTaskPopover(task, trigger, { actions: taskMoveActions(idx) });
  }

  async function executeReorder(fromIdx: number, toIdx: number) {
    const reordered = [...incompleteTasks];
    const [moved] = reordered.splice(fromIdx, 1);
    reordered.splice(toIdx, 0, moved);

    const newTaskIds = reordered.map((t) => t.id);
    const prevTasks = [...tasks];

    // Optimistic position update
    tasks = tasks.map((t) => {
      const newPos = newTaskIds.indexOf(t.id);
      return newPos >= 0 ? { ...t, position: newPos } : t;
    });
    setProjectTasks(project.id, tasks);

    dragIdx = null;
    dragOverIdx = null;

    try {
      const response = await fetch(`/projects/${project.id}/tasks/reorder`, {
        method: 'POST',
        headers: jsonHeaders(),
        body: JSON.stringify({ task_ids: newTaskIds }),
      });
      if (!response.ok) {
        tasks = prevTasks;
        setProjectTasks(project.id, prevTasks);
        toast.error('Failed to reorder tasks');
      }
    } catch {
      tasks = prevTasks;
      setProjectTasks(project.id, prevTasks);
      toast.error('Failed to reorder tasks');
    }
  }

  function confirmReorder() {
    if (!pendingReorder) return;
    const { fromIdx, toIdx } = pendingReorder;
    pendingReorder = null;
    // Switch to manual ordering first, then execute the reorder
    const manualPrefs: TaskViewPreferences = {
      sort_by: 'position',
      sort_direction: 'asc',
      group_by: 'none',
    };
    handlePrefsUpdate(manualPrefs);
    persistPrefs(manualPrefs);
    executeReorder(fromIdx, toIdx);
  }

  function cancelReorder() {
    pendingReorder = null;
  }

  function handleDragEnd() {
    dragIdx = null;
    dragOverIdx = null;
  }

  // Viewport tracking for responsive layout (debounced to avoid excessive re-renders)
  let windowWidth = $state(typeof window !== 'undefined' ? window.innerWidth : 1440);

  // eslint-disable-next-line momentium/no-unguarded-effect -- window resize listener with cleanup, no reactive deps
  $effect(() => {
    let timer: ReturnType<typeof setTimeout>;
    function onResize() {
      clearTimeout(timer);
      timer = setTimeout(() => {
        windowWidth = window.innerWidth;
      }, 100);
    }
    window.addEventListener('resize', onResize);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', onResize);
    };
  });

  let isMobile = $derived(windowWidth < 768);

  function handlePageClick() {
    if (openMenuId !== null) {
      openMenuId = null;
      movePickerTaskId = null;
    }
  }

  function handleKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape' && openMenuId !== null) {
      openMenuId = null;
      movePickerTaskId = null;
    }
  }

  const priorityIcons: Record<TaskPriority, string> = {
    none: '',
    low: '🔵',
    medium: '🟠',
    high: '🔴',
  };

  const priorityBorderColors: Record<TaskPriority, string> = {
    none: '#d1d5db',
    low: '#60a5fa',
    medium: '#fb923c',
    high: '#ef4444',
  };

  const priorityHoverBgs: Record<TaskPriority, string> = {
    none: 'rgba(209, 213, 219, 0.12)',
    low: 'rgba(96, 165, 250, 0.08)',
    medium: 'rgba(251, 146, 60, 0.08)',
    high: 'rgba(239, 68, 68, 0.08)',
  };

  const priorityLabels: Record<TaskPriority, string> = {
    none: 'None',
    low: 'Low',
    medium: 'Medium',
    high: 'High',
  };

  const statusLabels: Record<string, string> = {
    planned: 'Planned',
    active: 'Active',
    done: 'Done',
  };

  const statusIcons: Record<string, string> = {
    planned: '📋',
    active: '🔄',
    done: '✅',
  };

  const categoryOptions: { value: ProjectCategory; icon: string; label: string }[] = [
    { value: 'tech', icon: '💻', label: 'Tech' },
    { value: 'health', icon: '🏃', label: 'Health' },
    { value: 'personal', icon: '👤', label: 'Personal' },
    { value: 'finance', icon: '💰', label: 'Finance' },
    { value: 'travel', icon: '✈️', label: 'Travel' },
    { value: 'other', icon: '📋', label: 'Other' },
  ];

  const statusOptions: { value: ProjectStatus; icon: string; label: string }[] = [
    { value: 'planned', icon: '📋', label: 'Planned' },
    { value: 'active', icon: '🔄', label: 'Active' },
    { value: 'done', icon: '✅', label: 'Done' },
  ];

  // === Inline editing state ===

  // Local project state for optimistic updates (metadata only — tasks are
  // managed separately via the `tasks` state variable above).
  // WARNING: Do NOT read localProject.tasks — it is a stale shallow copy
  // from initialization. All task data flows through `tasks` / `incompleteTasks` /
  // `completedTasks` derived chains.
  // svelte-ignore state_referenced_locally
  let localProject = $state<ProjectWithTasks>({ ...project });

  // Plain variable (NOT $state) to track prop identity for re-sync detection.
  // Using $state here would wrap in a different proxy, making the reference
  // comparison always fail → infinite effect loop (see EFFECT_GUIDELINES.md Rule 8).
  // svelte-ignore state_referenced_locally
  let syncedProjectRef = project;

  // Re-sync from server props when Inertia re-delivers (navigation or same-project update)
  // eslint-disable-next-line momentium/no-unguarded-effect -- sync external Inertia props into local state on navigation
  $effect(() => {
    const incoming = project;
    if (incoming !== syncedProjectRef) {
      localProject = { ...incoming };
      syncedProjectRef = incoming;
    }
  });

  // Title editing
  let isEditingTitle = $state(false);
  let isEditingBreadcrumbTitle = $state(false);
  let editingTitle = $state('');
  let titleInputEl: HTMLInputElement | undefined = $state();
  let breadcrumbTitleInputEl: HTMLInputElement | undefined = $state();
  let isSavingTitle = $state(false);

  function startEditTitle() {
    editingTitle = localProject.title;
    isEditingTitle = true;
    tick().then(() => titleInputEl?.focus());
  }

  function startEditBreadcrumbTitle() {
    editingTitle = localProject.title;
    isEditingBreadcrumbTitle = true;
    tick().then(() => breadcrumbTitleInputEl?.focus());
  }

  async function saveTitle() {
    if (cancellingTitleEdit) return;
    const newTitle = editingTitle.trim();
    if (!newTitle || newTitle === localProject.title) {
      isEditingTitle = false;
      isEditingBreadcrumbTitle = false;
      return;
    }
    const prev = localProject.title;
    localProject.title = newTitle;
    isEditingTitle = false;
    isEditingBreadcrumbTitle = false;
    isSavingTitle = true;

    try {
      const response = await fetch(`/projects/${localProject.id}`, {
        method: 'PATCH',
        headers: jsonHeaders(),
        body: JSON.stringify({ project: { title: newTitle } }),
      });
      if (!response.ok) {
        localProject.title = prev;
        toast.error(await readApiErrorMessage(response, 'Failed to update title'));
      }
    } catch {
      localProject.title = prev;
      toast.error('Failed to update title');
    } finally {
      isSavingTitle = false;
    }
  }

  let cancellingTitleEdit = false;

  function cancelEditTitle() {
    cancellingTitleEdit = true;
    isEditingTitle = false;
    isEditingBreadcrumbTitle = false;
    editingTitle = localProject.title;
    tick().then(() => {
      cancellingTitleEdit = false;
    });
  }

  // Notes editing
  let isEditingNotes = $state(false);
  let editingNotes = $state('');
  let notesTextareaEl: HTMLTextAreaElement | undefined = $state();
  let isSavingNotes = $state(false);

  function startEditNotes() {
    editingNotes = localProject.notes || '';
    isEditingNotes = true;
    tick().then(() => {
      if (notesTextareaEl) {
        notesTextareaEl.focus();
        autoResizeTextarea(notesTextareaEl);
      }
    });
  }

  async function saveNotes() {
    const newNotes = editingNotes.trim() || undefined;
    if ((newNotes || '') === (localProject.notes || '')) {
      isEditingNotes = false;
      return;
    }
    const prev = localProject.notes;
    localProject.notes = newNotes;
    isEditingNotes = false;
    isSavingNotes = true;

    try {
      const response = await fetch(`/projects/${localProject.id}`, {
        method: 'PATCH',
        headers: jsonHeaders(),
        body: JSON.stringify({ project: { notes: newNotes || '' } }),
      });
      if (!response.ok) {
        localProject.notes = prev;
        toast.error('Failed to update notes');
      }
    } catch {
      localProject.notes = prev;
      toast.error('Failed to update notes');
    } finally {
      isSavingNotes = false;
    }
  }

  function autoResizeTextarea(el: HTMLTextAreaElement) {
    el.style.height = 'auto';
    el.style.height = el.scrollHeight + 'px';
  }

  // Status popover
  let statusPopoverOpen = $state(false);
  let statusBadgeEl: HTMLElement | undefined = $state();
  let statusPopoverEl: HTMLDivElement | undefined = $state();
  let statusPopoverStyle = $state('position: fixed; visibility: hidden;');
  let isSavingStatus = $state(false);

  async function positionStatusPopover() {
    if (!statusBadgeEl || !statusPopoverEl) return;
    const result = await computePosition(statusBadgeEl, statusPopoverEl, {
      strategy: 'fixed',
      placement: 'bottom-start',
      middleware: [offset(4), flip(), shift({ padding: 8 })],
    });
    statusPopoverStyle = `position: fixed; left: ${result.x}px; top: ${result.y}px; visibility: visible;`;
  }

  function toggleStatusPopover() {
    statusPopoverOpen = !statusPopoverOpen;
    if (statusPopoverOpen) {
      tick().then(positionStatusPopover);
    }
  }

  async function setStatus(newStatus: ProjectStatus) {
    if (newStatus === localProject.status) {
      statusPopoverOpen = false;
      return;
    }
    const prev = localProject.status;
    localProject.status = newStatus;
    statusPopoverOpen = false;
    isSavingStatus = true;

    try {
      const response = await fetch(`/projects/${localProject.id}/update_status`, {
        method: 'PATCH',
        headers: jsonHeaders(),
        body: JSON.stringify({ status: newStatus }),
      });
      if (!response.ok) {
        localProject.status = prev;
        toast.error('Failed to update status');
      }
    } catch {
      localProject.status = prev;
      toast.error('Failed to update status');
    } finally {
      isSavingStatus = false;
    }
  }

  // Category popover
  let categoryPopoverOpen = $state(false);
  let categoryBadgeEl: HTMLElement | undefined = $state();
  let categoryPopoverEl: HTMLDivElement | undefined = $state();
  let categoryPopoverStyle = $state('position: fixed; visibility: hidden;');
  let isSavingCategory = $state(false);

  async function positionCategoryPopover() {
    if (!categoryBadgeEl || !categoryPopoverEl) return;
    const result = await computePosition(categoryBadgeEl, categoryPopoverEl, {
      strategy: 'fixed',
      placement: 'bottom-start',
      middleware: [offset(4), flip(), shift({ padding: 8 })],
    });
    categoryPopoverStyle = `position: fixed; left: ${result.x}px; top: ${result.y}px; visibility: visible;`;
  }

  function toggleCategoryPopover() {
    categoryPopoverOpen = !categoryPopoverOpen;
    if (categoryPopoverOpen) {
      tick().then(positionCategoryPopover);
    }
  }

  async function setCategory(newCategory: ProjectCategory) {
    if (newCategory === localProject.category) {
      categoryPopoverOpen = false;
      return;
    }
    const prev = localProject.category;
    localProject.category = newCategory;
    categoryPopoverOpen = false;
    isSavingCategory = true;

    try {
      const response = await fetch(`/projects/${localProject.id}`, {
        method: 'PATCH',
        headers: jsonHeaders(),
        body: JSON.stringify({ project: { category: newCategory } }),
      });
      if (!response.ok) {
        localProject.category = prev;
        toast.error('Failed to update category');
      }
    } catch {
      localProject.category = prev;
      toast.error('Failed to update category');
    } finally {
      isSavingCategory = false;
    }
  }

  // Priority toggle
  let isSavingPriority = $state(false);

  async function toggleProjectPriority() {
    const prev = localProject.isPriority;
    localProject.isPriority = !prev;
    isSavingPriority = true;

    try {
      const response = await fetch(`/projects/${localProject.id}/toggle_priority`, {
        method: 'PATCH',
        headers: jsonHeaders(),
      });
      if (!response.ok) {
        localProject.isPriority = prev;
        toast.error('Failed to update priority');
      }
    } catch {
      localProject.isPriority = prev;
      toast.error('Failed to update priority');
    } finally {
      isSavingPriority = false;
    }
  }

  // More menu (⋯ dropdown)
  let moreMenuOpen = $state(false);
  let moreMenuBtnEl: HTMLElement | undefined = $state();
  let moreMenuEl: HTMLDivElement | undefined = $state();
  let moreMenuStyle = $state('position: fixed; visibility: hidden;');

  async function positionMoreMenu() {
    if (!moreMenuBtnEl || !moreMenuEl) return;
    const result = await computePosition(moreMenuBtnEl, moreMenuEl, {
      strategy: 'fixed',
      placement: 'bottom-end',
      middleware: [offset(4), flip(), shift({ padding: 8 })],
    });
    moreMenuStyle = `position: fixed; left: ${result.x}px; top: ${result.y}px; visibility: visible;`;
  }

  function toggleMoreMenu() {
    moreMenuOpen = !moreMenuOpen;
    if (moreMenuOpen) {
      tick().then(positionMoreMenu);
    }
  }

  function handleEditAllFields() {
    moreMenuOpen = false;
    openProjectModalForEdit(localProject as unknown as Project);
  }

  async function handleMoveProject() {
    moreMenuOpen = false;
    if (localProject.schedulingState === 'backlog') {
      // Move to month — use current month
      const now = new Date();
      const monthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
      try {
        const response = await fetch(`/projects/${localProject.id}/move_to_month`, {
          method: 'POST',
          headers: jsonHeaders(),
          body: JSON.stringify({ month_key: monthKey }),
        });
        if (response.ok) {
          localProject.schedulingState = 'scheduled';
          toast.success('Moved to current month');
        } else {
          toast.error('Failed to move project');
        }
      } catch {
        toast.error('Failed to move project');
      }
    } else {
      // Move to backlog
      try {
        const response = await fetch(`/projects/${localProject.id}/move_to_backlog`, {
          method: 'POST',
          headers: jsonHeaders(),
        });
        if (response.ok) {
          localProject.schedulingState = 'backlog';
          toast.success('Moved to backlog');
        } else {
          toast.error('Failed to move project');
        }
      } catch {
        toast.error('Failed to move project');
      }
    }
  }

  function handleArchiveToggle() {
    moreMenuOpen = false;

    const previous = {
      isArchived: !!localProject.isArchived,
      archivedAt: localProject.archivedAt ?? null,
    };
    const nextArchived = !localProject.isArchived;
    const optimistic = {
      isArchived: nextArchived,
      archivedAt: nextArchived ? new Date().toISOString() : null,
    };
    let committed = optimistic;
    const endpoint = previous.isArchived ? 'unarchive' : 'archive';
    const reverseEndpoint = nextArchived ? 'unarchive' : 'archive';

    const apply = (state: { isArchived: boolean; archivedAt: string | null }) => {
      localProject.isArchived = state.isArchived;
      localProject.archivedAt = state.archivedAt;
    };

    undoableAction({
      message: nextArchived ? 'Project archived' : 'Project unarchived',
      duration: 7000,
      onOptimisticApply: () => apply(optimistic),
      onCommit: async () => {
        const response = await fetch(`/projects/${localProject.id}/${endpoint}`, {
          method: 'PATCH',
          headers: jsonHeaders(),
        });
        if (!response.ok) throw new Error(await readApiErrorMessage(response));
        const updated = await response.json();
        committed = {
          isArchived: !!updated.isArchived,
          archivedAt: updated.archivedAt ?? null,
        };
        apply(committed);
      },
      onOptimisticUndo: () => apply(previous),
      onUndo: async () => {
        const response = await fetch(`/projects/${localProject.id}/${reverseEndpoint}`, {
          method: 'PATCH',
          headers: jsonHeaders(),
        });
        if (!response.ok) throw new Error(await readApiErrorMessage(response));
        const reverted = await response.json();
        apply({
          isArchived: !!reverted.isArchived,
          archivedAt: reverted.archivedAt ?? null,
        });
      },
      onCommitError: () => apply(previous),
      onUndoError: () => apply(committed),
      commitErrorMessage: 'Failed to update archive state',
      undoErrorMessage: 'Failed to undo archive state',
    });
  }

  function handleDeleteProject() {
    moreMenuOpen = false;
    const projectId = localProject.id;
    const projectTitle = localProject.title;

    undoableSoftDelete({
      message: `Deleted "${projectTitle}"`,
      onOptimisticRemove: () => {
        // No local project-list state on this page.
      },
      onOptimisticRestore: () => {
        // No local restore needed; server restore + reload handles it.
      },
      onDelete: async () => {
        const response = await fetch(`/projects/${projectId}`, {
          method: 'DELETE',
          headers: jsonHeaders(),
        });
        if (!response.ok) throw new Error('Server error');
        router.get('/projects');
      },
      onRestore: async () => {
        const response = await fetch(`/projects/${projectId}/undiscard`, {
          method: 'POST',
          headers: jsonHeaders(),
        });
        if (!response.ok) throw new Error('Server error');
        router.reload();
      },
    });
  }

  // Close popovers when clicking outside
  function handleHeaderClick(e: MouseEvent) {
    const target = e.target as HTMLElement;
    if (
      statusPopoverOpen &&
      statusPopoverEl &&
      !statusPopoverEl.contains(target) &&
      !statusBadgeEl?.contains(target)
    ) {
      statusPopoverOpen = false;
    }
    if (
      categoryPopoverOpen &&
      categoryPopoverEl &&
      !categoryPopoverEl.contains(target) &&
      !categoryBadgeEl?.contains(target)
    ) {
      categoryPopoverOpen = false;
    }
    if (
      moreMenuOpen &&
      moreMenuEl &&
      !moreMenuEl.contains(target) &&
      !moreMenuBtnEl?.contains(target)
    ) {
      moreMenuOpen = false;
    }
  }

  // Progress bar in header
  let headerTotal = $derived(incompleteTasks.length + completedTasks.length);
  let headerDoneCount = $derived(completedTasks.length);
  let headerPct = $derived(headerTotal > 0 ? Math.round((headerDoneCount / headerTotal) * 100) : 0);

  function formatDate(dateStr: string | null | undefined): string {
    if (!dateStr) return 'Not scheduled';
    const d = parseCalendarDate(dateStr);
    return formatCalendarDate(d, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  function formatScheduleRange(projectToFormat: ProjectWithTasks): string {
    if (projectToFormat.schedulingState === 'backlog' || !projectToFormat.startDate)
      return 'Not scheduled';
    if (!projectToFormat.endDate || projectToFormat.endDate === projectToFormat.startDate)
      return formatDate(projectToFormat.startDate);
    return `${formatDate(projectToFormat.startDate)} — ${formatDate(projectToFormat.endDate)}`;
  }

  // Task detail popover
  let popoverTask = $state<Task | null>(null);
  let popoverAnchorEl = $state<HTMLElement | null>(null);
  // Set when a row's ⋯ opens the task sheet on its menu (mobile), see openTaskMenu
  let popoverMenu = $state<'actions' | null>(null);
  let popoverMenuActions = $state<SheetAction[]>([]);

  function openTaskPopover(
    task: Task,
    anchorEl: HTMLElement,
    menu: { actions: SheetAction[] } | null = null,
  ) {
    popoverMenu = menu ? 'actions' : null;
    popoverMenuActions = menu?.actions ?? [];
    // Toggle off if clicking the same task
    if (popoverTask?.id === task.id) {
      closeTaskPopover();
      return;
    }
    popoverTask = task;
    popoverAnchorEl = anchorEl;
  }

  function closeTaskPopover() {
    popoverTask = null;
    popoverAnchorEl = null;
  }

  async function handlePopoverUpdate(task: Task, updates: Partial<Task>) {
    const prevTasks = [...tasks];

    // Optimistic update
    tasks = tasks.map((t) => (t.id === task.id ? { ...t, ...updates } : t));
    setProjectTasks(project.id, tasks);

    try {
      const response = await fetch(`/projects/${project.id}/tasks/${task.id}`, {
        method: 'PATCH',
        headers: jsonHeaders(),
        body: JSON.stringify({ task: updates }),
      });
      if (response.ok) {
        const updated: Task = await response.json();
        tasks = tasks.map((t) => (t.id === task.id ? updated : t));
        setProjectTasks(project.id, tasks);
        // Update popover task if still open
        if (popoverTask?.id === task.id) {
          popoverTask = updated;
        }
      } else {
        tasks = prevTasks;
        setProjectTasks(project.id, prevTasks);
        toast.error(await readApiErrorMessage(response, 'Failed to save changes'));
      }
    } catch {
      tasks = prevTasks;
      setProjectTasks(project.id, prevTasks);
      toast.error('Failed to save changes');
    }
  }
</script>

{#snippet showTaskRow(task: Task, idx: number)}
  <div
    class="task-row {$recentlyAddedTasks.has(task.id) ? 'just-added-task' : ''}"
    class:drag-over={dragOverIdx === idx && dragIdx !== idx}
    draggable={!isMobile && idx >= 0}
    ondragstart={(e) => !isMobile && idx >= 0 && handleDragStart(e, idx)}
    ondragover={(e) => idx >= 0 && handleDragOver(e, idx)}
    ondrop={(e) => idx >= 0 && handleDrop(e, idx)}
    ondragend={handleDragEnd}
    role="listitem"
  >
    {#if idx >= 0}
      <span class="drag-handle" title="Drag to reorder">⠿</span>
    {/if}
    <button
      type="button"
      class="task-checkbox"
      style="--cb-border: {priorityBorderColors[task.priority]}; --cb-hover-bg: {priorityHoverBgs[
        task.priority
      ]};"
      onclick={() => toggleTask(task)}
      title="Mark done"
    >
      <span class="checkbox-inner"></span>
    </button>

    <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
    <span
      class="task-title"
      onclick={(e) => openTaskPopover(task, e.currentTarget as HTMLElement)}
      role="button"
      tabindex="0">{task.title}</span
    >
    {#if task.notes}
      <span class="notes-indicator" title="Has notes">
        <svg viewBox="0 0 16 16" fill="currentColor">
          <path
            d="M4 1h8a1 1 0 011 1v12a1 1 0 01-1 1H4a1 1 0 01-1-1V2a1 1 0 011-1zm1 3v1h6V4H5zm0 3v1h6V7H5zm0 3v1h4v-1H5z"
          />
        </svg>
      </span>
    {/if}

    <div class="task-actions" class:menu-open={openMenuId === task.id}>
      <button
        type="button"
        class="task-menu-btn hover-reveal"
        onclick={(e) => {
          e.stopPropagation();
          openTaskMenu(task, idx, e.currentTarget as HTMLElement);
        }}
        title="Task actions"
        aria-label="Actions for {task.title}">⋯</button
      >
      <div
        class="task-menu"
        class:visible={openMenuId === task.id}
        class:picking={movePickerTaskId === task.id}
      >
        {#if movePickerTaskId === task.id}
          <MoveToProjectPanel
            excludeProjectId={project.id}
            onSelect={(id, title) => moveTaskToProject(task, id, title)}
            onBack={() => (movePickerTaskId = null)}
          />
        {:else}
          <!-- Touch without drag handles (e.g. a tablet): Move up / Move down -->
          {#if $tapAlternatives && idx >= 0}
            {#each taskMoveActions(idx) as move (move.label)}
              <button
                type="button"
                class="menu-item"
                onclick={() => {
                  closeMenus();
                  move.onSelect();
                }}
              >
                <Icon name={move.icon ?? 'more'} />{move.label}
              </button>
            {/each}
            <div class="menu-divider"></div>
          {/if}
          <div class="menu-label">Priority:</div>
          <div class="priority-row">
            <button
              type="button"
              class="priority-option"
              class:active={task.priority === 'none'}
              onclick={() => setPriority(task, 'none')}
              title="No priority">—</button
            >
            <button
              type="button"
              class="priority-option"
              class:active={task.priority === 'low'}
              onclick={() => setPriority(task, 'low')}
              title="Low">🔵</button
            >
            <button
              type="button"
              class="priority-option"
              class:active={task.priority === 'medium'}
              onclick={() => setPriority(task, 'medium')}
              title="Medium">🟠</button
            >
            <button
              type="button"
              class="priority-option"
              class:active={task.priority === 'high'}
              onclick={() => setPriority(task, 'high')}
              title="High">🔴</button
            >
          </div>
          <div class="menu-divider"></div>
          <button type="button" class="menu-item" onclick={() => handleDuplicateTask(task)}>
            <Icon name="duplicate" />Duplicate task
          </button>
          <TaskCopyActionRow {task} onDone={closeMenus} --copy-row-font-size="0.75rem" />
          <button type="button" class="menu-item" onclick={() => markWontDo(task)}>
            <Icon name="wont-do" />Won't do
          </button>
          <button
            type="button"
            class="menu-item menu-item-danger"
            onclick={() => requestDeleteTask(task)}
          >
            <Icon name="trash" />Delete
          </button>
          <div class="menu-divider"></div>
          <RecentMoveMenuItem
            currentProjectId={project.id}
            onMove={(projectId, projectTitle) => moveTaskToRecent(task, projectId, projectTitle)}
          />
          <button
            type="button"
            class="menu-item"
            onclick={(e) => {
              e.stopPropagation();
              movePickerTaskId = task.id;
            }}
          >
            <Icon name="move-to-project" />Move to project…
            <Icon name="chevron-right" size={12} class="menu-trail" />
          </button>
          <button type="button" class="menu-item" onclick={() => convertTaskToProject(task)}>
            <Icon name="convert" />Convert to project
          </button>
        {/if}
      </div>
    </div>
  </div>
{/snippet}

<!-- svelte-ignore a11y_no_static_element_interactions, a11y_click_events_have_key_events -->
<div
  class="show-page"
  onclick={(e) => {
    handlePageClick();
    handleHeaderClick(e);
  }}
  onkeydown={handleKeydown}
>
  <!-- Header -->
  {#if $viewport.isMobile}
    <MobileTopBar title={localProject.title} back="/projects" backLabel="Back to Projects" />
  {:else}
    <header class="page-header">
      <div class="header-left">
        <Link href="/" class="logo">
          <img src={momentiumLogo} alt="Momentium" class="logo-icon" />
        </Link>
        <NavDropdown activePage="projects" />
        <span class="breadcrumb-sep">/</span>
        <Link href="/projects" class="breadcrumb-link">Projects</Link>
        <span class="breadcrumb-sep">/</span>
        {#if isEditingBreadcrumbTitle}
          <input
            autocomplete="off"
            bind:this={breadcrumbTitleInputEl}
            bind:value={editingTitle}
            type="text"
            class="breadcrumb-title-input"
            aria-label="Project name"
            onblur={saveTitle}
            onkeydown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                saveTitle();
              } else if (e.key === 'Escape') {
                e.preventDefault();
                cancelEditTitle();
              }
            }}
          />
        {:else}
          <button
            type="button"
            class="breadcrumb-current"
            onclick={startEditBreadcrumbTitle}
            title="Click to edit project name">{localProject.title}</button
          >
        {/if}
      </div>
      <div class="header-right">
        <ProfileDropdown />
      </div>
    </header>
  {/if}

  <div class="page-body">
    <div class="content-container">
      <!-- Project Info Card (Interactive Header) -->
      <div class="project-card">
        <div class="project-header">
          <div class="project-meta">
            <!-- Clickable Category Badge -->
            <button
              type="button"
              class="category-badge clickable {categoryColors[localProject.category]}"
              class:saving={isSavingCategory}
              bind:this={categoryBadgeEl}
              onclick={toggleCategoryPopover}
              title="Click to change category"
            >
              {categoryIcons[localProject.category]}
              {localProject.category}
            </button>

            <!-- Clickable Status Badge -->
            <button
              type="button"
              class="status-badge clickable status-{localProject.status}"
              class:saving={isSavingStatus}
              bind:this={statusBadgeEl}
              onclick={toggleStatusPopover}
              title="Click to change status"
            >
              {statusIcons[localProject.status]}
              {statusLabels[localProject.status]}
            </button>

            {#if localProject.isArchived}
              <span class="archived-badge" title="This project is archived"
                ><Icon name="archive" size={11} />Archived</span
              >
            {/if}

            <!-- Clickable Priority Toggle -->
            <button
              type="button"
              class="priority-toggle-btn"
              class:active={localProject.isPriority}
              class:saving={isSavingPriority}
              onclick={toggleProjectPriority}
              title={localProject.isPriority ? 'Remove priority' : 'Set as priority'}
            >
              {localProject.isPriority ? '⭐' : '☆'}
            </button>
          </div>

          <!-- More Menu (⋯) -->
          <button
            type="button"
            class="more-menu-btn"
            bind:this={moreMenuBtnEl}
            onclick={toggleMoreMenu}
            title="More actions">⋯</button
          >
        </div>

        <!-- Click-to-edit Title -->
        <div class="title-wrapper">
          {#if isEditingTitle}
            <input
              autocomplete="off"
              bind:this={titleInputEl}
              bind:value={editingTitle}
              type="text"
              class="title-edit-input"
              onblur={saveTitle}
              onkeydown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  saveTitle();
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  cancelEditTitle();
                }
              }}
            />
          {:else}
            <h2 class="project-title editable" class:saving={isSavingTitle}>
              <button
                type="button"
                class="title-button"
                onclick={startEditTitle}
                title="Click to edit title"
              >
                {localProject.title}
                <span class="edit-affordance"><Icon name="edit" size={12} /></span>
              </button>
            </h2>
          {/if}
        </div>

        {#if localProject.prefix}
          <div class="project-prefix">Prefix: <strong>{localProject.prefix}</strong></div>
        {/if}

        <div class="project-details">
          <button
            type="button"
            class="schedule-summary-btn"
            onclick={() => openProjectScheduleModal(localProject as unknown as Project)}
            title="Adjust schedule"
          >
            <span class="schedule-summary-status">
              {localProject.schedulingState === 'backlog' ? '📥 Backlog' : '📅 Scheduled'}
            </span>
            <span class="schedule-summary-range">{formatScheduleRange(localProject)}</span>
            {#if localProject.startDate}
              <span class="schedule-summary-week">
                Week {getWeekKey(parseCalendarDate(localProject.startDate))}
              </span>
            {/if}
            <span class="schedule-summary-edit">Edit</span>
          </button>
        </div>

        <!-- Progress bar in header -->
        {#if headerTotal > 0}
          <div class="header-progress-row">
            <div class="header-progress-track">
              <div
                class="header-progress-fill"
                class:complete={headerPct === 100}
                style="width: {headerPct}%"
              ></div>
            </div>
            <span class="header-progress-label">
              {headerDoneCount}/{headerTotal} ({headerPct}%)
            </span>
          </div>
        {/if}

        <!-- Click-to-edit Notes -->
        <div class="project-notes-section">
          {#if isEditingNotes}
            <textarea
              bind:this={notesTextareaEl}
              bind:value={editingNotes}
              class="notes-edit-textarea"
              placeholder="Add notes..."
              onblur={saveNotes}
              oninput={(e) => autoResizeTextarea(e.currentTarget as HTMLTextAreaElement)}
              onkeydown={(e) => {
                if (e.key === 'Escape') {
                  e.preventDefault();
                  isEditingNotes = false;
                }
              }}></textarea>
          {:else}
            <button
              type="button"
              class="notes-display"
              class:saving={isSavingNotes}
              class:empty={!localProject.notes}
              onclick={startEditNotes}
              title="Click to edit notes"
            >
              {#if localProject.notes}
                <p>{localProject.notes}</p>
              {:else}
                <p class="notes-placeholder">Click to add notes...</p>
              {/if}
              <span class="edit-affordance"><Icon name="edit" size={12} /></span>
            </button>
          {/if}
        </div>
      </div>

      <!-- Tasks Card -->
      <div class="tasks-card" class:kanban-mode={viewMode === 'board'}>
        <div class="tasks-section-header">
          <div class="tasks-top-row">
            <h3>📋 Tasks</h3>
            <div class="tasks-top-right">
              <div class="view-mode-tabs" role="tablist" aria-label="Task view mode">
                <button
                  type="button"
                  role="tab"
                  class="view-mode-tab"
                  class:active={viewMode === 'list'}
                  aria-selected={viewMode === 'list'}
                  onclick={() => setViewMode('list')}
                  title="Checklist view"
                >
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                    <path d="M2 4h12v1H2zm0 3.5h12v1H2zm0 3.5h12v1H2z" />
                  </svg>
                  List
                </button>
                <button
                  type="button"
                  role="tab"
                  class="view-mode-tab"
                  class:active={viewMode === 'board'}
                  aria-selected={viewMode === 'board'}
                  onclick={() => setViewMode('board')}
                  title="Kanban board view"
                >
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                    <path d="M1 2h4v12H1zm5.5 0h4v8h-4zm5.5 0h4v10h-4z" opacity="0.8" />
                  </svg>
                  Board
                </button>
              </div>
              {#if viewMode === 'list'}
                <TaskViewOptions
                  preferences={taskViewPrefs}
                  onUpdate={handlePrefsUpdate}
                  getCopyTasks={() => taskGroups.flatMap((g) => g.tasks)}
                  copyHeading={localProject.title}
                  onBulkMoveClick={(anchorEl) => {
                    if (incompleteTasks.length === 0) return;
                    bulkMoveAnchorEl = anchorEl;
                    bulkMovePickerOpen = true;
                  }}
                />
              {/if}
            </div>
          </div>

          <!-- Active view indicator (list mode only) -->
          {#if viewMode === 'list' && (!canDragReorder || isGrouped)}
            <div class="active-view-chips">
              {#if taskViewPrefs.sort_by !== 'position'}
                <span class="view-chip">
                  {taskViewPrefs.sort_direction === 'desc' ? '↓' : '↑'}
                  {taskViewPrefs.sort_by === 'created_at'
                    ? 'Created'
                    : taskViewPrefs.sort_by === 'priority'
                      ? 'Priority'
                      : 'Title'}
                  <button
                    type="button"
                    class="chip-dismiss"
                    onclick={clearSortPref}
                    title="Clear sort">✕</button
                  >
                </span>
              {/if}
              {#if isGrouped}
                <span class="view-chip">
                  ▤ By {taskViewPrefs.group_by === 'priority' ? 'priority' : taskViewPrefs.group_by}
                  <button
                    type="button"
                    class="chip-dismiss"
                    onclick={clearGroupPref}
                    title="Clear grouping">✕</button
                  >
                </span>
              {/if}
            </div>
          {/if}
        </div>

        <!-- Add task (shared between list and board views) -->
        {#if $viewport.isMobile}
          <MobileAddTask onSubmit={createTask} />
        {:else}
          <div class="add-task-row" class:expanded={isAddFormExpanded}>
            <div class="add-task-title-row">
              <input
                autocomplete="off"
                bind:this={newTaskInput}
                bind:value={newTaskTitle}
                type="text"
                placeholder="Add a task..."
                class="add-task-input"
                onkeydown={(e) => {
                  if (e.key === 'Enter' && e.shiftKey) {
                    e.preventDefault();
                    if (!isAddFormExpanded) expandAddForm();
                    else addTask();
                  } else if (e.key === 'Enter') {
                    addTask();
                  } else if (e.key === 'Escape' && isAddFormExpanded) {
                    e.preventDefault();
                    collapseAddForm();
                    newTaskInput?.focus();
                  }
                }}
              />
              <button
                type="button"
                class="add-task-btn"
                onclick={addTask}
                disabled={!newTaskTitle.trim() || isAddingTask}
              >
                + Add
              </button>
            </div>
            {#if !isAddFormExpanded}
              <span class="add-task-hint" class:visible={!!newTaskTitle.trim()}>
                Shift + Enter or <button
                  type="button"
                  class="add-details-link"
                  tabindex={newTaskTitle.trim() ? 0 : -1}
                  aria-hidden={!newTaskTitle.trim()}
                  onclick={expandAddForm}>click here</button
                > to add details
              </span>
            {/if}

            {#if isAddFormExpanded}
              <textarea
                bind:this={newTaskNotesInput}
                bind:value={newTaskNotes}
                placeholder="Notes (optional)"
                class="add-task-notes"
                rows="3"
                onkeydown={(e) => {
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault();
                    addTask();
                  } else if (e.key === 'Escape') {
                    e.preventDefault();
                    collapseAddForm();
                    newTaskInput?.focus();
                  }
                }}></textarea>
              <div class="add-task-toolbar">
                <div class="add-task-priority-picker">
                  {#each [{ value: 'none' as TaskPriority, icon: '—', label: 'None' }, { value: 'low' as TaskPriority, icon: '🔵', label: 'Low' }, { value: 'medium' as TaskPriority, icon: '🟠', label: 'Medium' }, { value: 'high' as TaskPriority, icon: '🔴', label: 'High' }] as opt}
                    <button
                      type="button"
                      class="add-task-priority-option"
                      class:active={newTaskPriority === opt.value}
                      onclick={() => (newTaskPriority = opt.value)}
                      title={opt.label}
                    >
                      <span class="priority-icon">{opt.icon}</span>
                    </button>
                  {/each}
                </div>
                <div class="add-task-actions">
                  <button
                    type="button"
                    class="add-task-cancel"
                    onclick={() => {
                      collapseAddForm();
                      newTaskInput?.focus();
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    class="add-task-submit"
                    onclick={addTask}
                    disabled={!newTaskTitle.trim() || isAddingTask}
                  >
                    Add
                  </button>
                </div>
              </div>
            {/if}
          </div>
        {/if}

        {#if viewMode === 'list'}
          <!-- Incomplete tasks -->
          {#if incompleteTasks.length === 0 && completedTasks.length === 0}
            <div class="tasks-empty">
              <span>📝</span>
              <span>No tasks yet. Add your first task above.</span>
            </div>
          {:else if isGrouped}
            <!-- Grouped view -->
            {#each taskGroups as group (group.key)}
              {#if group.tasks.length > 0}
                <div class="task-group">
                  <div class="task-group-header">
                    <span class="task-group-icon">{group.icon}</span>
                    <span class="task-group-label">{group.label}</span>
                    <span class="task-group-count">{group.tasks.length}</span>
                    <CopyTasksMenu
                      tasks={group.tasks}
                      heading={`${localProject.title} — ${group.label}`}
                      label={`Copy ${group.label} tasks`}
                    />
                  </div>
                  <div class="task-list" role="list">
                    {#each group.tasks as task (task.id)}
                      {@render showTaskRow(task, -1)}
                    {/each}
                  </div>
                </div>
              {/if}
            {/each}
          {:else}
            <div class="task-list" role="list">
              {#each incompleteTasks as task, idx (task.id)}
                {@render showTaskRow(task, idx)}
              {/each}
            </div>
          {/if}

          <!-- Completed tasks -->
          {#if completedTasks.length > 0}
            <button
              type="button"
              class="completed-toggle"
              onclick={() => {
                showCompleted = !showCompleted;
                persistPrefs({ ...taskViewPrefs, show_completed: showCompleted });
              }}
            >
              <span class="toggle-arrow" class:open={showCompleted}>▶</span>
              Completed ({completedTasks.length})
            </button>

            {#if showCompleted}
              <div class="task-list completed-list" role="list">
                {#each completedTasks as task (task.id)}
                  <div class="task-row completed" role="listitem">
                    <span class="drag-handle-spacer"></span>
                    <button
                      type="button"
                      class="task-checkbox checked"
                      class:wont-do={task.status === 'wont_do'}
                      onclick={() => toggleTask(task)}
                      title={task.status === 'wont_do'
                        ? "Won't do — click to reopen"
                        : 'Done — click to reopen'}
                    >
                      {#if task.status === 'wont_do'}
                        <span class="checkbox-x">✕</span>
                      {:else}
                        <span class="checkbox-check">✓</span>
                      {/if}
                    </button>
                    <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
                    <span
                      class="task-title"
                      class:wont-do-text={task.status === 'wont_do'}
                      onclick={(e) => openTaskPopover(task, e.currentTarget as HTMLElement)}
                      role="button"
                      tabindex="0">{task.title}</span
                    >
                    {#if task.notes}
                      <span class="notes-indicator" title="Has notes">
                        <svg viewBox="0 0 16 16" fill="currentColor">
                          <path
                            d="M4 1h8a1 1 0 011 1v12a1 1 0 01-1 1H4a1 1 0 01-1-1V2a1 1 0 011-1zm1 3v1h6V4H5zm0 3v1h6V7H5zm0 3v1h4v-1H5z"
                          />
                        </svg>
                      </span>
                    {/if}
                    {#if task.completedAt}
                      <span class="completed-date">
                        {formatCalendarDate(new Date(task.completedAt), {
                          month: 'short',
                          day: 'numeric',
                        })}
                      </span>
                    {/if}
                    <div class="task-actions" class:menu-open={openMenuId === task.id}>
                      <button
                        type="button"
                        class="task-menu-btn hover-reveal"
                        onclick={(e) => {
                          e.stopPropagation();
                          openTaskMenu(task, -1, e.currentTarget as HTMLElement);
                        }}
                        title="Task actions"
                        aria-label="Actions for {task.title}">⋯</button
                      >
                      <div
                        class="task-menu"
                        class:visible={openMenuId === task.id}
                        class:picking={movePickerTaskId === task.id}
                      >
                        {#if movePickerTaskId === task.id}
                          <MoveToProjectPanel
                            excludeProjectId={project.id}
                            onSelect={(id, title) => moveTaskToProject(task, id, title)}
                            onBack={() => (movePickerTaskId = null)}
                          />
                        {:else}
                          <div class="menu-label">Priority:</div>
                          <div class="priority-row">
                            <button
                              type="button"
                              class="priority-option"
                              class:active={task.priority === 'none'}
                              onclick={() => setPriority(task, 'none')}
                              title="No priority">—</button
                            >
                            <button
                              type="button"
                              class="priority-option"
                              class:active={task.priority === 'low'}
                              onclick={() => setPriority(task, 'low')}
                              title="Low">🔵</button
                            >
                            <button
                              type="button"
                              class="priority-option"
                              class:active={task.priority === 'medium'}
                              onclick={() => setPriority(task, 'medium')}
                              title="Medium">🟠</button
                            >
                            <button
                              type="button"
                              class="priority-option"
                              class:active={task.priority === 'high'}
                              onclick={() => setPriority(task, 'high')}
                              title="High">🔴</button
                            >
                          </div>
                          <div class="menu-divider"></div>
                          <button
                            type="button"
                            class="menu-item"
                            onclick={() => handleDuplicateTask(task)}
                          >
                            <Icon name="duplicate" />Duplicate task
                          </button>
                          <TaskCopyActionRow
                            {task}
                            onDone={closeMenus}
                            --copy-row-font-size="0.75rem"
                          />
                          <button type="button" class="menu-item" onclick={() => toggleTask(task)}>
                            <Icon name="reopen" />Reopen
                          </button>
                          <button
                            type="button"
                            class="menu-item menu-item-danger"
                            onclick={() => requestDeleteTask(task)}
                          >
                            <Icon name="trash" />Delete
                          </button>
                          <div class="menu-divider"></div>
                          <RecentMoveMenuItem
                            currentProjectId={project.id}
                            onMove={(projectId, projectTitle) =>
                              moveTaskToRecent(task, projectId, projectTitle)}
                          />
                          <button
                            type="button"
                            class="menu-item"
                            onclick={(e) => {
                              e.stopPropagation();
                              movePickerTaskId = task.id;
                            }}
                          >
                            <Icon name="move-to-project" />Move to project…
                            <Icon name="chevron-right" size={12} class="menu-trail" />
                          </button>
                          <button
                            type="button"
                            class="menu-item"
                            onclick={() => convertTaskToProject(task)}
                          >
                            <Icon name="convert" />Convert to project
                          </button>
                        {/if}
                      </div>
                    </div>
                  </div>
                {/each}
              </div>
            {/if}
          {/if}
        {:else}
          <!-- Kanban board view -->
          <ProjectKanbanBoard
            {tasks}
            projectId={project.id}
            {project}
            onTasksChange={handleTasksChangeFromKanban}
          />
        {/if}
      </div>
    </div>
  </div>
</div>

{#if bulkMovePickerOpen && bulkMoveAnchorEl}
  <MoveToProjectPicker
    excludeProjectId={project.id}
    anchorEl={bulkMoveAnchorEl}
    onSelect={(id, title) => {
      bulkMoveTargetProjectId = id;
      bulkMoveTargetProjectTitle = title;
      bulkMovePickerOpen = false;
      showBulkMoveConfirm = true;
    }}
    onClose={() => {
      bulkMovePickerOpen = false;
    }}
  />
{/if}

<BulkMoveTasksConfirmModal
  open={showBulkMoveConfirm}
  sourceProjectTitle={project.title}
  targetProjectTitle={bulkMoveTargetProjectTitle}
  taskCount={incompleteTasks.length}
  onCancel={() => {
    showBulkMoveConfirm = false;
    bulkMoveTargetProjectId = null;
    bulkMoveTargetProjectTitle = '';
  }}
  onConfirm={confirmBulkMoveIncompleteTasks}
/>

<ProjectModal />

<!-- Status picker popover -->
{#if statusPopoverOpen}
  <!-- svelte-ignore a11y_no_static_element_interactions -->
  <div
    class="picker-popover"
    style={statusPopoverStyle}
    bind:this={statusPopoverEl}
    onmousedown={(e) => e.stopPropagation()}
  >
    <div class="picker-label">Status</div>
    {#each statusOptions as opt}
      <button
        type="button"
        class="picker-option"
        class:active={localProject.status === opt.value}
        onclick={() => setStatus(opt.value)}
      >
        <span class="picker-icon">{opt.icon}</span>
        <span class="picker-text">{opt.label}</span>
        {#if localProject.status === opt.value}
          <span class="picker-check">✓</span>
        {/if}
      </button>
    {/each}
  </div>
{/if}

<!-- Category picker popover -->
{#if categoryPopoverOpen}
  <!-- svelte-ignore a11y_no_static_element_interactions -->
  <div
    class="picker-popover"
    style={categoryPopoverStyle}
    bind:this={categoryPopoverEl}
    onmousedown={(e) => e.stopPropagation()}
  >
    <div class="picker-label">Category</div>
    {#each categoryOptions as opt}
      <button
        type="button"
        class="picker-option"
        class:active={localProject.category === opt.value}
        onclick={() => setCategory(opt.value)}
      >
        <span class="picker-icon">{opt.icon}</span>
        <span class="picker-text">{opt.label}</span>
        {#if localProject.category === opt.value}
          <span class="picker-check">✓</span>
        {/if}
      </button>
    {/each}
  </div>
{/if}

<!-- More actions menu -->
{#if moreMenuOpen}
  <!-- svelte-ignore a11y_no_static_element_interactions -->
  <div
    class="more-menu-popover"
    style={moreMenuStyle}
    bind:this={moreMenuEl}
    onmousedown={(e) => e.stopPropagation()}
  >
    <button type="button" class="more-menu-item" onclick={handleEditAllFields}>
      <Icon name="edit" />Edit all fields...
    </button>
    <button type="button" class="more-menu-item" onclick={handleMoveProject}>
      {#if localProject.schedulingState === 'backlog'}
        <Icon name="calendar" />Move to month
      {:else}
        <Icon name="backlog" />Move to backlog
      {/if}
    </button>
    <button type="button" class="more-menu-item" onclick={handleArchiveToggle}>
      {#if localProject.isArchived}
        <Icon name="unarchive" />Unarchive project
      {:else}
        <Icon name="archive" />Archive project
      {/if}
    </button>
    <div class="more-menu-divider"></div>
    <button type="button" class="more-menu-item danger" onclick={handleDeleteProject}>
      <Icon name="trash" />Delete project
    </button>
  </div>
{/if}

<!-- Task detail popover -->
<TaskDetailPopover
  task={popoverTask}
  anchorEl={popoverAnchorEl}
  {project}
  initialMenu={popoverMenu}
  menuActions={popoverMenuActions}
  onClose={closeTaskPopover}
  onUpdate={handlePopoverUpdate}
  onDelete={(task, pendingUpdates) => {
    if (pendingUpdates) handlePopoverUpdate(task, pendingUpdates);
    closeTaskPopover();
    deleteTask(task);
  }}
  onMarkWontDo={(task, pendingUpdates) => {
    closeTaskPopover();
    markWontDoWithUpdates(task, pendingUpdates);
  }}
  onToggleComplete={(task, pendingUpdates) => {
    closeTaskPopover();
    toggleTaskWithUpdates(task, pendingUpdates);
  }}
/>

<!-- Reorder confirmation modal -->
{#if pendingReorder}
  {@const sortLabel =
    taskViewPrefs.sort_by === 'created_at' ? 'date created' : taskViewPrefs.sort_by}
  <!-- svelte-ignore a11y_no_static_element_interactions, a11y_click_events_have_key_events -->
  <div class="confirm-overlay" onclick={cancelReorder}>
    <!-- svelte-ignore a11y_no_static_element_interactions, a11y_click_events_have_key_events -->
    <div class="confirm-dialog" onclick={(e) => e.stopPropagation()}>
      <p class="confirm-message">Switch to manual ordering?</p>
      <p class="confirm-subtext">
        Tasks are currently sorted by <strong>{sortLabel}</strong>{#if isGrouped}
          and grouped by <strong>{taskViewPrefs.group_by}</strong>{/if}. Moving this task will clear
        those settings and switch to manual drag ordering.
      </p>
      <div class="confirm-actions">
        <button type="button" class="confirm-cancel-btn" onclick={cancelReorder}>Cancel</button>
        <button type="button" class="confirm-ok-btn" onclick={confirmReorder}>Switch & Move</button>
      </div>
    </div>
  </div>
{/if}

<style>
  .show-page {
    display: flex;
    flex-direction: column;
    min-height: 100%;
    background: #f9fafb;
  }

  .page-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.375rem 0.75rem;
    background: white;
    border-bottom: 1px solid #e5e7eb;
  }

  .header-left {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .header-right {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  :global(.logo) {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    font-size: 0.875rem;
    font-weight: 700;
    color: #1f2937;
    text-decoration: none;
    flex-shrink: 0;
  }

  .logo-icon {
    width: 1.25rem;
    height: 1.25rem;
    object-fit: contain;
  }

  .breadcrumb-sep {
    color: #d1d5db;
    font-size: 0.75rem;
  }

  :global(.page-header .breadcrumb-link) {
    font-size: 0.75rem;
    color: #6b7280;
    text-decoration: none;
  }

  :global(.page-header .breadcrumb-link:hover) {
    color: #2563eb;
  }

  .breadcrumb-current {
    font-size: 0.75rem;
    font-weight: 600;
    color: #1f2937;
    padding: 0.125rem 0.25rem;
    border-radius: 0.1875rem;
    max-width: 20rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    background: none;
    border: none;
    font-family: inherit;
    cursor: text;
  }

  .breadcrumb-current:hover {
    background: #f3f4f6;
  }

  .breadcrumb-title-input {
    width: min(20rem, 30vw);
    font-size: 0.75rem;
    font-weight: 600;
    font-family: inherit;
    color: #1f2937;
    padding: 0.125rem 0.25rem;
    border: 1px solid #2563eb;
    border-radius: 0.1875rem;
    outline: none;
  }

  .page-body {
    flex: 1;
    padding: 1.5rem;
  }

  .content-container {
    max-width: 48rem;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    transition: max-width 0.25s ease;
  }

  .show-page:has(.tasks-card.kanban-mode) .content-container {
    max-width: 100%;
  }

  .project-card {
    background: white;
    border-radius: 0.5rem;
    padding: 1.25rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  }

  .project-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 0.75rem;
  }

  .project-meta {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    flex-wrap: wrap;
  }

  .category-badge {
    padding: 0.125rem 0.5rem;
    border-radius: 0.25rem;
    font-size: 0.6875rem;
    font-weight: 500;
  }

  .category-badge.clickable {
    cursor: pointer;
    border: 1px solid transparent;
    transition: all 0.15s;
  }

  .category-badge.clickable:hover {
    filter: brightness(0.95);
    border-color: rgba(0, 0, 0, 0.1);
  }

  .category-badge.saving {
    opacity: 0.6;
  }

  .status-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.125rem 0.5rem;
    border-radius: 1rem;
    font-size: 0.6875rem;
    font-weight: 500;
  }

  .status-badge.clickable {
    cursor: pointer;
    border: 1px solid transparent;
    transition: all 0.15s;
  }

  .status-badge.clickable:hover {
    filter: brightness(0.95);
    border-color: rgba(0, 0, 0, 0.1);
  }

  .status-badge.saving {
    opacity: 0.6;
  }

  .status-planned {
    background: #eff6ff;
    color: #2563eb;
  }
  .status-active {
    background: #fffbeb;
    color: #d97706;
  }
  .status-almost {
    background: #fff7ed;
    color: #ea580c;
  }
  .status-done {
    background: #f0fdf4;
    color: #16a34a;
  }

  .archived-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.2rem 0.55rem;
    border-radius: 9999px;
    font-size: 0.74rem;
    font-weight: 600;
    color: #334155;
    background: #e2e8f0;
    border: 1px solid #cbd5e1;
  }

  .project-card:has(.archived-badge) {
    border-color: #cbd5e1;
    background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  }

  .priority-toggle-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.5rem;
    height: 1.5rem;
    font-size: 0.875rem;
    background: none;
    border: 1px solid transparent;
    border-radius: 0.25rem;
    cursor: pointer;
    transition: all 0.15s;
    padding: 0;
    line-height: 1;
  }

  .priority-toggle-btn:hover {
    background: #fefce8;
    border-color: #fde68a;
  }

  .priority-toggle-btn.active {
    background: #fefce8;
  }

  .priority-toggle-btn.saving {
    opacity: 0.6;
  }

  .more-menu-btn {
    width: 2rem;
    height: 2rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1rem;
    color: #6b7280;
    background: none;
    border: 1px solid #d1d5db;
    border-radius: 0.375rem;
    cursor: pointer;
    transition: all 0.15s;
    letter-spacing: 0.05em;
  }

  .more-menu-btn:hover {
    color: #374151;
    background: #f9fafb;
    border-color: #9ca3af;
  }

  /* Title inline editing */
  .title-wrapper {
    margin-bottom: 0.5rem;
  }

  .project-title {
    font-size: 1.25rem;
    font-weight: 700;
    color: #1f2937;
    margin: 0;
    position: relative;
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    cursor: pointer;
    padding: 0.125rem 0.25rem;
    border-radius: 0.25rem;
    transition: background 0.15s;
  }

  .title-button {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    font-size: inherit;
    font-weight: inherit;
    color: inherit;
    font-family: inherit;
    background: none;
    border: none;
    padding: 0.125rem 0.25rem;
    border-radius: 0.25rem;
    cursor: pointer;
    transition: background 0.15s;
    text-align: left;
    line-height: 1.3;
  }

  .title-button:hover {
    background: rgba(59, 130, 246, 0.06);
  }

  .title-button:hover .edit-affordance {
    opacity: 0.5;
  }

  .project-title.saving {
    opacity: 0.6;
  }

  .edit-affordance {
    display: inline-flex;
    color: #9ca3af;
    font-size: 0.625rem;
    opacity: 0;
    transition: opacity 0.15s;
    flex-shrink: 0;
  }

  .title-edit-input {
    width: 100%;
    font-size: 1.25rem;
    font-weight: 700;
    color: #1f2937;
    padding: 0.125rem 0.25rem;
    background: transparent;
    border: none;
    border-bottom: 2px solid #3b82f6;
    border-radius: 0;
    outline: none;
    font-family: inherit;
    line-height: 1.3;
  }

  .project-prefix {
    font-size: 0.75rem;
    color: #6b7280;
    margin-bottom: 0.5rem;
  }

  .project-details {
    display: flex;
    margin-bottom: 0.5rem;
  }

  .schedule-summary-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    max-width: 100%;
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;
    background: #f9fafb;
    color: #374151;
    cursor: pointer;
    padding: 0.375rem 0.625rem;
    font-size: 0.75rem;
    text-align: left;
    transition:
      background 120ms ease,
      border-color 120ms ease,
      color 120ms ease;
  }

  .schedule-summary-btn:hover {
    background: #eff6ff;
    border-color: #bfdbfe;
    color: #1d4ed8;
  }

  .schedule-summary-status {
    font-weight: 600;
    white-space: nowrap;
  }

  .schedule-summary-range {
    font-weight: 500;
  }

  .schedule-summary-week,
  .schedule-summary-edit {
    color: #6b7280;
    white-space: nowrap;
  }

  .schedule-summary-edit {
    font-weight: 600;
  }

  /* Header progress bar */
  .header-progress-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 0.75rem;
    margin-bottom: 0.25rem;
  }

  .header-progress-track {
    flex: 1;
    height: 6px;
    background: #e5e7eb;
    border-radius: 3px;
    overflow: hidden;
  }

  .header-progress-fill {
    height: 100%;
    background: #3b82f6;
    border-radius: 3px;
    transition:
      width 0.3s ease,
      background 0.3s ease;
    min-width: 0;
  }

  .header-progress-fill.complete {
    background: #16a34a;
  }

  .header-progress-label {
    font-size: 0.6875rem;
    color: #6b7280;
    font-variant-numeric: tabular-nums;
    flex-shrink: 0;
    font-weight: 500;
  }

  /* Notes inline editing */
  .project-notes-section {
    margin-top: 0.75rem;
    padding-top: 0.75rem;
    border-top: 1px solid #f3f4f6;
  }

  .notes-display {
    position: relative;
    display: flex;
    align-items: flex-start;
    gap: 0.375rem;
    cursor: pointer;
    padding: 0.25rem;
    border-radius: 0.25rem;
    transition: background 0.15s;
    min-height: 1.5rem;
    background: none;
    border: none;
    width: 100%;
    text-align: left;
    font-family: inherit;
  }

  .notes-display:hover {
    background: rgba(59, 130, 246, 0.06);
  }

  .notes-display:hover .edit-affordance {
    opacity: 0.5;
  }

  .notes-display.saving {
    opacity: 0.6;
  }

  .notes-display p {
    flex: 1;
    font-size: 0.8125rem;
    color: #374151;
    line-height: 1.5;
    white-space: pre-wrap;
    margin: 0;
  }

  .notes-placeholder {
    color: #9ca3af !important;
    font-style: italic;
  }

  .notes-edit-textarea {
    width: 100%;
    padding: 0.375rem 0.25rem;
    font-size: 0.8125rem;
    color: #374151;
    background: rgba(59, 130, 246, 0.04);
    border: none;
    border-bottom: 2px solid #3b82f6;
    border-radius: 0.25rem 0.25rem 0 0;
    outline: none;
    resize: none;
    line-height: 1.5;
    font-family: inherit;
    min-height: 2.5rem;
  }

  .notes-edit-textarea::placeholder {
    color: #9ca3af;
  }

  /* Picker popovers (status/category) */
  .picker-popover {
    z-index: 200;
    min-width: 10rem;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    padding: 0.25rem;
    display: flex;
    flex-direction: column;
  }

  .picker-label {
    font-size: 0.625rem;
    font-weight: 600;
    color: #9ca3af;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    padding: 0.375rem 0.5rem 0.25rem;
  }

  .picker-option {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.375rem 0.5rem;
    font-size: 0.75rem;
    color: #374151;
    background: none;
    border: none;
    border-radius: 0.25rem;
    cursor: pointer;
    text-align: left;
    transition: background 0.1s;
    width: 100%;
  }

  .picker-option:hover {
    background: #f3f4f6;
  }

  .picker-option.active {
    background: #eff6ff;
    color: #2563eb;
  }

  .picker-icon {
    font-size: 0.8125rem;
    flex-shrink: 0;
  }

  .picker-text {
    flex: 1;
    font-weight: 500;
  }

  .picker-check {
    font-size: 0.625rem;
    color: #2563eb;
    font-weight: 700;
  }

  /* More actions menu */
  .more-menu-popover {
    z-index: 200;
    min-width: 12rem;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    padding: 0.25rem;
    display: flex;
    flex-direction: column;
  }

  .more-menu-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 0.625rem;
    font-size: 0.75rem;
    color: #374151;
    background: none;
    border: none;
    border-radius: 0.25rem;
    cursor: pointer;
    text-align: left;
    transition: background 0.1s;
    width: 100%;
  }

  .more-menu-item:hover {
    background: #f3f4f6;
  }

  .more-menu-item :global(.icon) {
    color: #6b7280;
  }

  .more-menu-item.danger :global(.icon) {
    color: inherit;
  }

  .more-menu-item.danger {
    color: #dc2626;
  }

  .more-menu-item.danger:hover {
    background: #fef2f2;
  }

  .more-menu-divider {
    height: 1px;
    background: #e5e7eb;
    margin: 0.125rem 0;
  }

  /* Tasks Card */
  .tasks-card {
    background: white;
    border-radius: 0.5rem;
    padding: 1.25rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  }

  .tasks-card.kanban-mode {
    display: flex;
    flex-direction: column;
    min-height: 28rem;
  }

  .tasks-section-header {
    margin-bottom: 0.75rem;
  }

  .kanban-mode .tasks-section-header {
    margin-bottom: 0;
  }

  .tasks-top-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0.375rem;
  }

  .tasks-top-row h3 {
    font-size: 0.9375rem;
    font-weight: 700;
    color: #1f2937;
  }

  .tasks-top-right {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  /* ─── View Mode Tabs ─── */
  .view-mode-tabs {
    display: flex;
    background: #f1f5f9;
    border-radius: 0.375rem;
    padding: 0.125rem;
    gap: 0.0625rem;
  }

  .view-mode-tab {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.25rem 0.5rem;
    font-size: 0.6875rem;
    font-weight: 500;
    color: #64748b;
    background: transparent;
    border: none;
    border-radius: 0.25rem;
    cursor: pointer;
    transition: all 0.12s;
    white-space: nowrap;
  }

  .view-mode-tab:hover {
    color: #334155;
    background: rgba(255, 255, 255, 0.5);
  }

  .view-mode-tab.active {
    color: #1e293b;
    background: white;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
    font-weight: 600;
  }

  .view-mode-tab svg {
    flex-shrink: 0;
  }

  .active-view-chips {
    display: flex;
    gap: 0.25rem;
    flex-wrap: wrap;
    margin-bottom: 0.25rem;
  }

  .view-chip {
    display: inline-flex;
    align-items: center;
    gap: 0.1875rem;
    padding: 0.125rem 0.25rem 0.125rem 0.375rem;
    font-size: 0.625rem;
    font-weight: 500;
    color: #2563eb;
    background: #eff6ff;
    border: 1px solid #bfdbfe;
    border-radius: 0.25rem;
  }

  .chip-dismiss {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 0.875rem;
    height: 0.875rem;
    font-size: 0.5625rem;
    color: #93c5fd;
    background: none;
    border: none;
    border-radius: 0.125rem;
    cursor: pointer;
    padding: 0;
    line-height: 1;
    transition: all 0.1s;
  }

  .chip-dismiss:hover {
    color: #1d4ed8;
    background: rgba(37, 99, 235, 0.1);
  }

  /* Grouped view */
  .task-group {
    margin-bottom: 0.75rem;
  }

  .task-group-header {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.25rem 0.5rem;
    margin-bottom: 0.25rem;
    border-bottom: 1px solid #f3f4f6;
  }

  .task-group-icon {
    font-size: 0.6875rem;
  }

  .task-group-label {
    font-size: 0.6875rem;
    font-weight: 600;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }

  .task-group-count {
    font-size: 0.625rem;
    color: #9ca3af;
    margin-left: auto;
  }

  .add-task-row {
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
  }

  .add-task-row.expanded {
    border: 1px solid #d1d5db;
    border-radius: 0.5rem;
    padding: 0.5rem;
    background: #fafbff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  }

  .add-task-title-row {
    display: flex;
    gap: 0.375rem;
    align-items: center;
  }

  .add-task-input {
    flex: 1;
    padding: 0.5rem 0.75rem;
    font-size: 0.8125rem;
    border: 1px solid #d1d5db;
    border-radius: 0.375rem;
    outline: none;
    transition: border-color 0.15s;
    min-width: 0;
  }

  .add-task-row.expanded .add-task-input {
    border-color: transparent;
    background: transparent;
    padding-left: 0.25rem;
    font-weight: 600;
  }

  .add-task-input:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.15);
  }

  .add-task-row.expanded .add-task-input:focus {
    border-color: transparent;
    box-shadow: none;
  }

  .add-task-hint {
    font-size: 0.625rem;
    color: #9ca3af;
    white-space: nowrap;
    user-select: none;
    line-height: 1;
    height: 0.875rem;
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
    transition: opacity 0.15s;
  }

  .add-task-hint.visible {
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
  }

  .add-details-link {
    background: none;
    border: none;
    padding: 0;
    font: inherit;
    color: #3b82f6;
    cursor: pointer;
    text-decoration: underline;
    text-underline-offset: 1px;
  }

  .add-details-link:hover {
    color: #2563eb;
  }

  .add-task-btn {
    padding: 0.5rem 0.75rem;
    font-size: 0.75rem;
    font-weight: 600;
    color: white;
    background: #3b82f6;
    border: none;
    border-radius: 0.375rem;
    cursor: pointer;
    transition: background 0.15s;
    flex-shrink: 0;
  }

  .add-task-row.expanded .add-task-btn {
    display: none;
  }

  .add-task-btn:hover:not(:disabled) {
    background: #2563eb;
  }
  .add-task-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  .add-task-notes {
    width: 100%;
    padding: 0.5rem;
    font-size: 0.8125rem;
    color: #4b5563;
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 0.375rem;
    outline: none;
    resize: none;
    line-height: 1.5;
    font-family: inherit;
    transition:
      border-color 0.15s,
      box-shadow 0.15s;
  }

  .add-task-notes:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 1px rgba(59, 130, 246, 0.25);
  }

  .add-task-notes::placeholder {
    color: #9ca3af;
  }

  .add-task-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.375rem;
    border-top: 1px solid #e5e7eb;
    padding-top: 0.375rem;
  }

  .add-task-priority-picker {
    display: flex;
    gap: 0.1875rem;
  }

  .add-task-priority-option {
    width: 1.5rem;
    height: 1.375rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.625rem;
    background: none;
    border: 1px solid transparent;
    border-radius: 0.25rem;
    cursor: pointer;
    transition: all 0.1s;
    padding: 0;
  }

  .add-task-priority-option:hover {
    background: #f3f4f6;
    border-color: #d1d5db;
  }

  .add-task-priority-option.active {
    background: #eff6ff;
    border-color: #3b82f6;
  }

  .add-task-actions {
    display: flex;
    gap: 0.375rem;
    align-items: center;
  }

  .add-task-cancel {
    padding: 0.25rem 0.5rem;
    font-size: 0.625rem;
    font-weight: 500;
    color: #6b7280;
    background: none;
    border: none;
    border-radius: 0.25rem;
    cursor: pointer;
    transition: all 0.1s;
  }

  .add-task-cancel:hover {
    color: #374151;
    background: #f3f4f6;
  }

  .add-task-submit {
    padding: 0.25rem 0.625rem;
    font-size: 0.625rem;
    font-weight: 600;
    color: white;
    background: #3b82f6;
    border: none;
    border-radius: 0.25rem;
    cursor: pointer;
    transition: background 0.15s;
  }

  .add-task-submit:hover:not(:disabled) {
    background: #2563eb;
  }

  .add-task-submit:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  .tasks-empty {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    justify-content: center;
    padding: 2rem 0;
    font-size: 0.8125rem;
    color: #9ca3af;
  }

  .task-list {
    display: flex;
    flex-direction: column;
    gap: 0.125rem;
  }

  .task-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.375rem 0.5rem;
    border-radius: 0.375rem;
    transition: background 0.1s;
  }

  .task-row:hover {
    background: #f9fafb;
  }

  .task-row.drag-over {
    border-top: 2px solid #3b82f6;
  }

  .task-row.completed > .task-title {
    color: #9ca3af;
  }

  .task-row.completed > .drag-handle-spacer,
  .task-row.completed > .task-checkbox,
  .task-row.completed > .notes-indicator,
  .task-row.completed > .completed-date {
    opacity: 0.5;
  }

  .drag-handle {
    width: 1rem;
    font-size: 0.75rem;
    color: #d1d5db;
    cursor: grab;
    user-select: none;
    flex-shrink: 0;
    text-align: center;
    transition: color 0.15s;
  }

  .drag-handle:hover {
    color: #9ca3af;
  }

  .drag-handle-spacer {
    width: 1rem;
    flex-shrink: 0;
  }

  .task-checkbox {
    width: 1.125rem;
    height: 1.125rem;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 2px solid var(--cb-border, #d1d5db);
    border-radius: 0.25rem;
    background: white;
    cursor: pointer;
    flex-shrink: 0;
    transition: all 0.15s;
    padding: 0;
  }

  .task-checkbox:hover {
    background: var(--cb-hover-bg, rgba(209, 213, 219, 0.12));
  }

  .task-checkbox.checked {
    background: #3b82f6;
    border-color: #3b82f6;
  }

  .task-checkbox.wont-do {
    background: #9ca3af;
    border-color: #9ca3af;
  }

  .checkbox-inner {
    width: 0;
    height: 0;
  }

  .checkbox-check,
  .checkbox-x {
    font-size: 0.625rem;
    color: white;
    font-weight: 700;
  }

  .task-title {
    flex: 1;
    font-size: 0.8125rem;
    color: #1f2937;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    cursor: pointer;
    padding: 0.125rem 0.375rem;
    border-radius: 0.25rem;
    transition: background 0.1s;
  }

  .task-title:hover {
    background: rgba(59, 130, 246, 0.08);
  }

  .task-title.wont-do-text {
    text-decoration: line-through;
    color: #9ca3af;
  }

  .notes-indicator {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 0.875rem;
    height: 0.875rem;
    color: #9ca3af;
    flex-shrink: 0;
    margin-right: 0.25rem;
  }

  .notes-indicator svg {
    width: 100%;
    height: 100%;
  }

  /* Task actions dropdown */
  .task-actions {
    position: relative;
  }

  .task-menu-btn {
    width: 1.5rem;
    height: 1.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.8125rem;
    color: #9ca3af;
    background: none;
    border: none;
    border-radius: 0.25rem;
    cursor: pointer;
    opacity: 0;
    transition: all 0.15s;
  }

  .task-row:hover .task-menu-btn {
    opacity: 1;
    color: #6b7280;
  }

  .task-menu-btn:hover {
    color: #374151;
    background: rgba(0, 0, 0, 0.06);
  }

  .task-menu-btn:focus-visible {
    opacity: 1;
    color: #374151;
    outline: 2px solid #3b82f6;
    outline-offset: 1px;
  }

  .menu-open .task-menu-btn {
    opacity: 1;
  }

  .task-menu {
    position: absolute;
    top: 100%;
    right: 0;
    margin-top: 0.125rem;
    min-width: 12rem;
    max-height: 18rem;
    overflow-y: auto;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 0.375rem;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    z-index: 60;
    opacity: 0;
    visibility: hidden;
    transition:
      opacity 0.1s,
      visibility 0.1s;
  }

  .task-menu.visible {
    opacity: 1;
    visibility: visible;
  }

  .menu-label {
    padding: 0.3125rem 0.5rem;
    font-size: 0.625rem;
    color: #9ca3af;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }

  .priority-row {
    display: flex;
    gap: 0.125rem;
    padding: 0.125rem 0.5rem 0.25rem;
  }

  .priority-option {
    width: 1.5rem;
    height: 1.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.6875rem;
    background: none;
    border: 1px solid transparent;
    border-radius: 0.25rem;
    cursor: pointer;
    transition: all 0.1s;
  }

  .priority-option:hover {
    background: #f3f4f6;
    border-color: #d1d5db;
  }

  .priority-option.active {
    background: #eff6ff;
    border-color: #3b82f6;
  }

  .menu-divider {
    height: 1px;
    background: #e5e7eb;
    margin: 0.125rem 0;
  }

  .menu-item {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    width: 100%;
    padding: 0.375rem 0.5rem;
    font-size: 0.75rem;
    color: #374151;
    background: none;
    border: none;
    cursor: pointer;
    text-align: left;
    transition: background 0.1s;
  }

  .menu-item:hover {
    background: #f3f4f6;
  }

  .menu-item-danger {
    color: #dc2626;
  }

  .menu-item-danger:hover {
    background: #fef2f2;
  }

  .menu-item :global(.icon) {
    color: #6b7280;
  }

  .menu-item-danger :global(.icon) {
    color: inherit;
  }

  .menu-item :global(.menu-trail) {
    margin-left: auto;
    color: #9ca3af;
  }

  /* "Move to project…" swaps the menu's items for a project search. */
  .task-menu.picking {
    width: 16rem;
    max-height: none;
    overflow: hidden;
  }

  .completed-toggle {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    width: 100%;
    padding: 0.5rem 0;
    margin-top: 0.75rem;
    font-size: 0.75rem;
    font-weight: 600;
    color: #6b7280;
    background: none;
    border: none;
    border-top: 1px solid #f3f4f6;
    cursor: pointer;
    text-align: left;
  }

  .completed-toggle:hover {
    color: #374151;
  }

  .toggle-arrow {
    font-size: 0.5625rem;
    transition: transform 0.15s;
  }

  .toggle-arrow.open {
    transform: rotate(90deg);
  }

  .completed-list {
    margin-top: 0.25rem;
  }

  .completed-date {
    font-size: 0.625rem;
    color: #9ca3af;
    flex-shrink: 0;
  }

  /* Delete confirmation modal */
  .confirm-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.4);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 100;
  }

  .confirm-dialog {
    background: white;
    border-radius: 0.5rem;
    padding: 1.5rem;
    min-width: 20rem;
    max-width: 24rem;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  }

  .confirm-message {
    font-size: 0.875rem;
    color: #1f2937;
    margin-bottom: 0.25rem;
  }

  .confirm-subtext {
    font-size: 0.75rem;
    color: #9ca3af;
    margin-bottom: 1rem;
  }

  .confirm-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
  }

  .confirm-cancel-btn {
    padding: 0.375rem 0.75rem;
    font-size: 0.75rem;
    font-weight: 500;
    color: #374151;
    background: #f3f4f6;
    border: 1px solid #d1d5db;
    border-radius: 0.375rem;
    cursor: pointer;
    transition: background 0.15s;
  }

  .confirm-cancel-btn:hover {
    background: #e5e7eb;
  }

  .confirm-ok-btn {
    padding: 0.375rem 0.75rem;
    font-size: 0.75rem;
    font-weight: 600;
    color: white;
    background: #3b82f6;
    border: none;
    border-radius: 0.375rem;
    cursor: pointer;
    transition: background 0.15s;
  }

  .confirm-ok-btn:hover {
    background: #2563eb;
  }

  .confirm-ok-btn.danger {
    background: #dc2626;
  }

  .confirm-ok-btn.danger:hover {
    background: #b91c1c;
  }

  /* === Responsive: Mobile (<768px) === */
  @media (max-width: 767px) {
    .show-page {
      min-width: 0;
    }

    .page-header {
      padding: 0.375rem 0.75rem;
    }

    .header-left {
      gap: 0.375rem;
      min-width: 0;
      overflow: hidden;
    }

    .breadcrumb-sep {
      flex-shrink: 0;
    }

    .breadcrumb-current {
      max-width: 10rem;
      font-size: 0.6875rem;
    }

    :global(.logo) {
      flex-shrink: 0;
    }

    .page-body {
      padding: 0.75rem;
    }

    .content-container {
      max-width: 100%;
    }

    .tasks-card.kanban-mode {
      min-height: auto;
    }

    .project-card {
      padding: 1rem;
    }

    /* Category / status / ★ on one row, wrapping only if they don't fit */
    .project-meta {
      gap: 0.375rem;
      min-width: 0;
    }

    .project-details {
      flex-direction: column;
      gap: 0.5rem;
    }

    /* Schedule: "📅 Scheduled · Week … Edit" row, date range on its own line */
    .schedule-summary-btn {
      display: grid;
      grid-template-columns: auto 1fr auto;
      grid-template-areas:
        'status week edit'
        'range range range';
      align-items: baseline;
      column-gap: 0.375rem;
      row-gap: 0.125rem;
      width: 100%;
      padding: 0.5rem 0.625rem;
    }

    .schedule-summary-status {
      grid-area: status;
    }

    .schedule-summary-week {
      grid-area: week;
    }

    .schedule-summary-week::before {
      content: '· ';
    }

    .schedule-summary-edit {
      grid-area: edit;
    }

    .schedule-summary-range {
      grid-area: range;
    }

    /* Title (!important: overrides the global 16px mobile input rule) */
    .title-edit-input {
      font-size: 1.125rem !important;
    }

    .title-button {
      font-size: 1.125rem;
    }

    /* More menu button — larger touch target */
    .more-menu-btn {
      width: 2.75rem;
      height: 2.75rem;
    }

    /* Tasks card */
    .tasks-card {
      padding: 1rem;
    }

    /* Task rows — min 44px touch target */
    .task-row {
      min-height: 2.75rem;
      padding: 0.5rem;
      gap: 0.375rem;
    }

    /* Hide drag handles on mobile */
    .drag-handle {
      display: none;
    }

    .drag-handle-spacer {
      display: none;
    }

    /* Task action menu — always visible on mobile (no hover) */
    .task-menu-btn {
      opacity: 1;
      color: #6b7280;
      width: 2.75rem;
      height: 2.75rem;
      font-size: 1rem;
    }

    /* Task checkbox — larger touch target */
    .task-checkbox {
      width: 1.375rem;
      height: 1.375rem;
    }

    /* Task title — ensure adequate touch target */
    .task-title {
      font-size: 0.875rem;
      padding: 0.25rem 0.375rem;
    }

    /* Add task input — full-width, larger padding for touch */
    .add-task-input {
      font-size: 1rem;
      padding: 0.625rem 0.75rem;
      min-height: 2.75rem;
    }

    .add-task-btn {
      min-height: 2.75rem;
      padding: 0.5rem 1rem;
      font-size: 0.8125rem;
    }

    /* Completed toggle — larger touch target */
    .completed-toggle {
      min-height: 2.75rem;
      font-size: 0.8125rem;
      padding: 0.625rem 0;
    }

    /* Badges — larger touch targets for inline editing */
    .category-badge.clickable,
    .status-badge.clickable {
      min-height: 2rem;
      padding: 0.25rem 0.625rem;
    }

    /* ★ stays visually small; ::after expands the hit area to 44×44 */
    .priority-toggle-btn {
      position: relative;
      width: 2rem;
      height: 2rem;
      font-size: 1rem;
    }

    .priority-toggle-btn::after {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      width: var(--mobile-tap);
      height: var(--mobile-tap);
      transform: translate(-50%, -50%);
    }

    /* Notes section */
    .notes-display {
      min-height: 2.75rem;
      padding: 0.375rem;
    }

    .notes-edit-textarea {
      font-size: 1rem;
      min-height: 3rem;
    }

    /* Picker popovers — ensure they don't overflow */
    .picker-popover,
    .more-menu-popover {
      max-width: calc(100vw - 1.5rem);
    }

    .picker-option,
    .more-menu-item {
      min-height: 2.75rem;
    }

    /* Confirm dialog — responsive */
    .confirm-dialog {
      min-width: 0;
      max-width: calc(100vw - 2rem);
      margin: 0 1rem;
    }

    /* Task list: 44px rows. The 18px checkbox keeps its look; ::after expands the hit area. */
    .task-row {
      padding-block: 0;
    }

    .task-checkbox {
      position: relative;
    }

    .task-checkbox::after {
      content: '';
      position: absolute;
      inset: -13px;
    }

    .task-title {
      padding-block: 0.75rem;
    }

    .view-mode-tab {
      min-height: var(--mobile-tap);
      padding-inline: 0.75rem;
    }

    /* View chips — touch targets */
    .view-chip {
      min-height: 1.75rem;
    }

    .chip-dismiss {
      width: 1.25rem;
      height: 1.25rem;
    }
  }
</style>
